import type { TranslationFunction } from '@/app/components/translations/Translation';

/**
 * Curated offers include checked bags per booking, so the tooltip has to name the booking
 * count once more than one bag is included. Keys are spelled out instead of built from a
 * template so translationsSearch.js can still see them.
 */
const GROUP_LUGGAGE_TOOLTIP_KEYS = {
  checked: {
    single: 'vacation.oss.cards.flights.luggage.group.tooltip.checked',
    plural: 'vacation.oss.cards.flights.luggage.group.tooltip.checked.plural',
  },
  cabinAndChecked: {
    single: 'vacation.oss.cards.flights.luggage.group.tooltip.cabinandchecked',
    plural: 'vacation.oss.cards.flights.luggage.group.tooltip.cabinandchecked.plural',
  },
  handAndChecked: {
    single: 'vacation.oss.cards.flights.luggage.group.tooltip.handandchecked',
    plural: 'vacation.oss.cards.flights.luggage.group.tooltip.handandchecked.plural',
  },
} as const;

export type GroupLuggageTooltipVariant = keyof typeof GROUP_LUGGAGE_TOOLTIP_KEYS;

/**
 * Falls back to the singular copy while a plural key has no value: `t` returns '' for a key
 * SmartText has not published yet, and a blank tooltip is worse than an imprecise one.
 */
export const getGroupLuggageTooltipText = ({
  variant,
  numberOfGroupLuggagePieces,
  t,
}: {
  variant: GroupLuggageTooltipVariant;
  numberOfGroupLuggagePieces: number;
  t: TranslationFunction;
}): string => {
  const keys = GROUP_LUGGAGE_TOOLTIP_KEYS[variant];

  if (numberOfGroupLuggagePieces > 1) {
    return t(keys.plural, { bags: numberOfGroupLuggagePieces.toString() }) || t(keys.single);
  }

  return t(keys.single);
};
